// ========================================
// pages/Features.js - Detailed Features Overview
// ========================================
// PROJECT: Agyapadie Vault - Digital Inheritance Platform
// LAST MODIFIED: April 12, 2026
// 
// PURPOSE: Comprehensive list of all platform features
// 
// SECTIONS:
// - Hero: Page title and description
// - Security Features
// - Inheritance Management
// - Asset Management
// - Beneficiary Management
// - Trustee System
// - Verification & Integrity
// - Platform Features
// - Call to Action
// ========================================

import React from 'react';
import { Link } from 'react-router-dom';

const Features = () => {
  const featureCategories = [
    {
      title: "🔒 Bank-Grade Security",
      icon: "/images/icons/feature-security.png",
      features: [
        "AES-256 encryption for all data at rest",
        "TLS 1.3 encryption for data in transit",
        "Cryptographic hashing with SHA-256",
        "Per-user salt for password hashing (bcrypt)",
        "Secure key management with regular rotation",
        "Blockchain-based integrity verification"
      ]
    },
    {
      title: "👨‍👩‍👧‍👦 Inheritance Management",
      icon: "/images/icons/feature-tradition.png",
      features: [
        "Global Next of Kin (NOK) designation",
        "External beneficiaries per asset",
        "Default inheritance to all NOKs",
        "Customizable beneficiary selection per asset",
        "Legacy preservation for future generations",
        "Ghanaian family structure support"
      ]
    },
    {
      title: "🏦 Multi-Asset Support",
      icon: "/images/icons/asset-bank.png",
      features: [
        "Bank accounts with encrypted balance",
        "Real estate properties with title deeds",
        "Vehicles with VIN tracking",
        "Investment portfolios",
        "Cryptocurrency wallets",
        "Digital assets and documents",
        "Last Will & Testament storage"
      ]
    },
    {
      title: "⚖️ Trustee Verification System",
      icon: "/images/icons/feature-trustees.png",
      features: [
        "Multi-trustee death verification",
        "Customizable verification thresholds",
        "Automated email notifications",
        "90-day dormancy detection",
        "Trustee removal after 365 days no-response",
        "Trustee invitation and acceptance flow"
      ]
    },
    {
      title: "📜 Asset Certificates",
      icon: "/images/icons/asset-certificate.png",
      features: [
        "Unique certificate number per asset",
        "Cryptographic hash for integrity",
        "PDF certificate download",
        "Tamper-proof verification",
        "Internal blockchain explorer (coming soon)",
        "Certificate sharing capability"
      ]
    },
    {
      title: "🔐 Data Privacy",
      icon: "/images/icons/feature-fraud.png",
      features: [
        "Semi-anonymous account creation",
        "No ID verification required",
        "Private notes only visible to you",
        "Description visible only to NOKs after death",
        "GDPR and Data Protection Act 843 compliant",
        "No data selling to third parties"
      ]
    }
  ];

  return (
    <div className="features-page">
      
      {/* ======================================== */}
      {/* SECTION 1: HERO */}
      {/* ======================================== */}
      <section className="features-hero">
        <div className="features-hero-overlay"></div>
        <div className="features-container">
          <div className="features-hero-content">
            <div className="features-badge">
              <img src="/images/symbols/golden-stool.png" alt="Golden Stool" className="features-badge-icon" />
              <span>Powerful Features</span>
            </div>
            <h1 className="features-hero-title">
              Everything You Need to <span className="gold">Protect Your Legacy</span>
            </h1>
            <p className="features-hero-subtitle">
              Agyapadie Vault combines traditional Ghanaian inheritance values with modern 
              cryptographic technology to ensure your assets reach the right hands.
            </p>
          </div>
        </div>
      </section>

      {/* ======================================== */}
      {/* SECTION 2: FEATURE CATEGORIES */}
      {/* ======================================== */}
      <section className="features-grid-section">
        <div className="features-container">
          <div className="features-grid">
            {featureCategories.map((category, idx) => (
              <div key={idx} className="feature-category-card">
                <img src={category.icon} alt={category.title} className="feature-category-icon" />
                <h2 className="feature-category-title">{category.title}</h2>
                <ul className="feature-list">
                  {category.features.map((feature, fIdx) => (
                    <li key={fIdx}>
                      <span className="feature-check">✓</span>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ======================================== */}
      {/* SECTION 3: COMPARISON TABLE */}
      {/* ======================================== */}
      <section className="features-comparison">
        <div className="features-container">
          <h2 className="features-section-title">Plan <span className="gold">Comparison</span></h2>
          <p className="features-section-subtitle">
            Compare features across all plans - one-time payment, lifetime access
          </p>
          
          <div className="comparison-table-wrapper">
            <table className="comparison-table">
              <thead>
                <tr>
                  <th>Feature</th>
                  <th>Free</th>
                  <th>Premium</th>
                  <th>Business</th>
                  <th>Enterprise</th>
                </tr>
              </thead>
              <tbody>
                <tr><td>Global Next of Kin</td><td>2</td><td>4</td><td>6</td><td>10</td></tr>
                <tr><td>Total Assets</td><td>10</td><td>50</td><td>100</td><td>400</td></tr>
                <tr><td>Trustees</td><td>2</td><td>3</td><td>6</td><td>10</td></tr>
                <tr><td>External Beneficiaries (per asset)</td><td>0</td><td>1</td><td>2</td><td>3</td></tr>
                <tr><td>File Upload Size</td><td>2MB</td><td>5MB</td><td>10MB</td><td>20MB</td></tr>
                <tr><td>Verification Threshold Options</td><td>1</td><td>1-2 of 3</td><td>1-3 of 6</td><td>1-4 of 10</td></tr>
                <tr><td>Priority Support</td><td>-</td><td>-</td><td>✓ Email</td><td>✓ 24/7</td></tr>
                <tr><td>Custom Integration</td><td>-</td><td>-</td><td>-</td><td>✓</td></tr>
                <tr className="price-row"><td>Price (One-Time)</td><td>GHS 0</td><td>GHS 399</td><td>GHS 999</td><td>GHS 2,999</td></tr>
              </tbody>
            </table>
          </div>
          
          <div className="comparison-note">
            <p>💡 All plans are <strong>ONE-TIME PAYMENT</strong> - pay once, use forever. No recurring fees.</p>
          </div>
        </div>
      </section>

      {/* ======================================== */}
      {/* SECTION 4: CALL TO ACTION */}
      {/* ======================================== */}
      <section className="features-cta">
        <div className="features-container">
          <div className="features-cta-content">
            <h2 className="features-cta-title">Ready to Get Started?</h2>
            <p className="features-cta-subtitle">
              Join thousands of Ghanaian families who trust Agyapadie Vault to protect their legacy.
            </p>
            <div className="features-cta-buttons">
              <Link to="/register" className="features-cta-primary">Create Free Account</Link>
              <Link to="/pricing" className="features-cta-secondary">View Pricing →</Link>
            </div>
          </div>
        </div>
      </section>

      <style dangerouslySetInnerHTML={{ __html: `
        .features-page {
          font-family: 'Montserrat', sans-serif;
          background: white;
        }
        
        .features-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 20px;
        }
        
        .gold { color: #FFD700; }
        
        /* Hero Section */
        .features-hero {
          background: #0A0A0A;
          position: relative;
          padding: 80px 0;
          text-align: center;
        }
        
        .features-hero-overlay {
          position: absolute;
          inset: 0;
          background: url('/images/patterns/hero-pattern.png');
          opacity: 0.05;
          pointer-events: none;
        }
        
        .features-hero-content {
          position: relative;
          z-index: 2;
        }
        
        .features-badge {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          background: rgba(255,215,0,0.1);
          padding: 6px 16px;
          border-radius: 30px;
          margin-bottom: 24px;
        }
        
        .features-badge-icon {
          height: 20px;
          width: auto;
        }
        
        .features-badge span {
          color: #FFD700;
          font-size: 14px;
        }
        
        .features-hero-title {
          font-size: 52px;
          font-weight: bold;
          color: white;
          margin-bottom: 20px;
        }
        
        .features-hero-subtitle {
          font-size: 18px;
          color: #ccc;
          max-width: 700px;
          margin: 0 auto;
          line-height: 1.6;
        }
        
        /* Feature Grid */
        .features-grid-section {
          padding: 80px 0;
          background: white;
        }
        
        .features-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 30px;
        }
        
        .feature-category-card {
          background: #F5F0E6;
          padding: 30px;
          border-radius: 15px;
          transition: transform 0.3s;
        }
        
        .feature-category-card:hover {
          transform: translateY(-5px);
        }
        
        .feature-category-icon {
          height: 60px;
          width: auto;
          margin-bottom: 20px;
        }
        
        .feature-category-title {
          font-size: 20px;
          margin-bottom: 20px;
          color: #2E7D32;
        }
        
        .feature-list {
          list-style: none;
          padding: 0;
        }
        
        .feature-list li {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 12px;
          font-size: 14px;
          color: #444;
        }
        
        .feature-check {
          color: #FFD700;
          font-weight: bold;
          font-size: 16px;
        }
        
        /* Comparison Table */
        .features-comparison {
          padding: 80px 0;
          background: #F5F0E6;
        }
        
        .features-section-title {
          font-size: 36px;
          text-align: center;
          margin-bottom: 20px;
          color: #0A0A0A;
        }
        
        .features-section-subtitle {
          font-size: 18px;
          text-align: center;
          color: #666;
          margin-bottom: 50px;
        }
        
        .comparison-table-wrapper {
          overflow-x: auto;
        }
        
        .comparison-table {
          width: 100%;
          border-collapse: collapse;
          background: white;
          border-radius: 10px;
          overflow: hidden;
        }
        
        .comparison-table th,
        .comparison-table td {
          padding: 15px;
          text-align: center;
          border-bottom: 1px solid #eee;
        }
        
        .comparison-table th:first-child,
        .comparison-table td:first-child {
          text-align: left;
          font-weight: bold;
          background: #F5F0E6;
        }
        
        .comparison-table th {
          background: #1A1A1A;
          color: white;
          font-weight: bold;
        }
        
        .comparison-table tr:hover td {
          background: #FFF8E1;
        }
        
        .price-row td {
          background: #FFF8E1;
          font-weight: bold;
          color: #FFD700;
        }
        
        .comparison-note {
          text-align: center;
          margin-top: 30px;
          font-size: 14px;
          color: #666;
        }
        
        /* CTA */
        .features-cta {
          padding: 80px 0;
          background: linear-gradient(135deg, #1A1A1A 0%, #0A0A0A 100%);
          text-align: center;
        }
        
        .features-cta-content {
          max-width: 700px;
          margin: 0 auto;
        }
        
        .features-cta-title {
          font-size: 32px;
          color: white;
          margin-bottom: 20px;
        }
        
        .features-cta-subtitle {
          font-size: 18px;
          color: #ccc;
          margin-bottom: 30px;
        }
        
        .features-cta-buttons {
          display: flex;
          gap: 20px;
          justify-content: center;
          flex-wrap: wrap;
        }
        
        .features-cta-primary {
          padding: 14px 32px;
          background: #FFD700;
          color: #0A0A0A;
          text-decoration: none;
          border-radius: 5px;
          font-weight: bold;
          transition: all 0.3s;
        }
        
        .features-cta-secondary {
          padding: 14px 32px;
          background: transparent;
          border: 2px solid #FFD700;
          color: #FFD700;
          text-decoration: none;
          border-radius: 5px;
          font-weight: bold;
          transition: all 0.3s;
        }
        
        @media (max-width: 1024px) {
          .features-grid {
            grid-template-columns: repeat(2, 1fr);
          }
          .features-hero-title { font-size: 40px; }
        }
        
        @media (max-width: 768px) {
          .features-grid { grid-template-columns: 1fr; }
          .features-hero-title { font-size: 28px; }
          .features-section-title { font-size: 24px; }
          .comparison-table th, .comparison-table td { padding: 10px; font-size: 12px; }
        }
      `}} />
    </div>
  );
};

export default Features;